import { Link } from "react-router-dom";
import BrandLogo from "./components/BrandLogo";
import { useAuth } from "./context/AuthContext";

const highlights = [
  {
    title: "Track Content Revenue",
    text: "Connect affiliate links to TikToks, Shorts, blog posts, emails, and campaigns so every click has context."
  },
  {
    title: "Find Winning Platforms",
    text: "See which channels drive clicks, conversions, EPC, and estimated revenue without digging through spreadsheets."
  },
  {
    title: "Optimize Creator Income",
    text: "Use revenue-first dashboards, milestones, and team visibility to keep momentum tied to real creator outcomes."
  }
];

const trustPills = [
  "TikTok, YouTube, Blog, X, Email",
  "Content-level revenue tracking",
  "Mobile-friendly creator analytics",
  "No enterprise-tool clutter"
];

const setupSteps = [
  "Choose your platform",
  "Name the content",
  "Add the affiliate URL",
  "Watch revenue by source"
];

export default function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="container marketing-shell marketing-page">
      <section className="hero-card marketing-hero">
        <div className="hero-copy">
          <span className="eyebrow">Track / Analyze / Grow</span>
          <BrandLogo />
          <h1>The affiliate analytics platform built for creators.</h1>
          <p className="hero-lead">
            AffiTrack helps creators see exactly which content, platform, and campaign turns attention into affiliate
            revenue.
          </p>

          <div className="cta-row">
            <Link className="cta-button" to={user ? "/dashboard" : "/login"}>
              {user ? "View Dashboard" : "Start Free"}
            </Link>
            <Link className="ghost-link" to="/pricing">
              View Pricing
            </Link>
          </div>

          <div className="hero-pill-row">
            <span className="hero-pill">Basic: 3 content links</span>
            <span className="hero-pill">Pro: 25 content links</span>
            <span className="hero-pill">Team: 5 seats / 100 content links</span>
          </div>
        </div>

        <div className="hero-art-panel">
          <div className="hero-art-logo">
            <BrandLogo compact />
          </div>
          <div className="art-panel-card">
            <div className="mini-chart">
              <span />
              <span />
              <span />
              <span />
            </div>
            <p>Creator revenue analytics</p>
          </div>
        </div>
      </section>

      <section className="feature-grid marketing-feature-grid">
        {highlights.map((item) => (
          <article className="card feature-card" key={item.title}>
            <BrandLogo compact />
            <h2>{item.title}</h2>
            <p>{item.text}</p>
          </article>
        ))}
      </section>

      <section className="feature-grid trust-strip">
        {trustPills.map((item) => (
          <article className="card feature-card trust-strip-card" key={item}>
            <span className="hero-pill">{item}</span>
          </article>
        ))}
      </section>

      <section className="card split-showcase">
        <div className="split-copy">
          <span className="eyebrow">Built For Your Stage</span>
          <h2>Know which content actually makes money.</h2>
          <p>
            Most affiliate tools are built for agencies, media buyers, or complex ad-tech workflows. AffiTrack is for
            TikTok creators, YouTubers, bloggers, newsletter operators, and solo affiliates who need clean income
            signals.
          </p>
          <div className="showcase-tags">
            <span>Top content</span>
            <span>Platform revenue</span>
            <span>EPC by source</span>
          </div>
          <Link className="cta-button" to={user ? "/dashboard" : "/login"}>
            {user ? "Open Workspace" : "Start Basic Free"}
          </Link>
        </div>

        <div className="showcase-panel">
          <div className="showcase-screen">
            <div className="screen-header">
              <span />
              <span />
              <span />
            </div>
            <div className="screen-body">
              <div className="screen-stat">
                <strong>$1.2k</strong>
                <span>Creator revenue</span>
              </div>
              <div className="screen-stat">
                <strong>$3.48</strong>
                <span>EPC</span>
              </div>
              <div className="screen-line" />
            </div>
          </div>
        </div>
      </section>

      <section className="card split-showcase">
        <div className="split-copy">
          <span className="eyebrow">Mentor Teams</span>
          <h2>Made for creator teams, coaches, and small affiliate groups.</h2>
          <p>
            Use invites, shared creator analytics, member leaderboard views, and admin oversight to help a small team
            grow revenue without overwhelming them with agency-style tooling.
          </p>
          <div className="showcase-tags">
            <span>Team scoreboards</span>
            <span>Shared analytics</span>
            <span>Member goals</span>
          </div>
          <Link className="ghost-link" to={user ? "/team" : "/pricing"}>
            {user ? "View Team Workspace" : "See Team Plan"}
          </Link>
        </div>

        <div className="card workspace-hero">
          <span className="eyebrow">Simple Setup</span>
          <h2>Keep the workflow simple from day one.</h2>
          <div className="feature-grid setup-grid">
            {setupSteps.map((step) => (
              <article className="card feature-card compact-feature-card" key={step}>
                <h3>{step}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="card trust-panel">
        <div>
          <span className="eyebrow">Support And Trust</span>
          <h2>See clicks, conversions, EPC, and estimated profit without enterprise complexity.</h2>
          <p>
            AffiTrack gives creators and mentor-led teams the clarity they need to spot winning content, understand
            platform performance, and keep momentum moving from one shared workspace.
          </p>
        </div>

        <div className="trust-list">
          <div className="trust-item">
            <strong>Support</strong>
            <span>support@affitrack.net</span>
          </div>
          <div className="trust-item">
            <strong>Cancellation</strong>
            <span>Cancel before the next monthly cycle</span>
          </div>
          <div className="trust-item">
            <strong>Refunds</strong>
            <span>Reviewed case by case for duplicate or incorrect billing</span>
          </div>
        </div>
      </section>

      <section className="hero-card final-cta-card">
        <span className="eyebrow">Start Simple</span>
        <h2>Start tracking the content that drives revenue.</h2>
        <p className="hero-lead">
          AffiTrack helps creators, solo affiliates, and mentor-led teams turn affiliate links into a clean creator
          income dashboard.
        </p>
        <div className="cta-row">
          <Link className="cta-button" to={user ? "/dashboard" : "/login"}>
            {user ? "Go To Workspace" : "Start Free"}
          </Link>
          <Link className="ghost-link" to="/pricing">
            View Pricing
          </Link>
        </div>
      </section>
    </div>
  );
}
